import { useQuery } from "@tanstack/react-query";
import { OptionSelect } from "Common/Components";
import { useBranchStore } from "feature/configuration/branch/store";
import { useFormik } from "formik";
import { NotificationType, showNotifications } from "helpers/toas_notification";
import { FormBranch, RequestBranch } from "interfaces/Branch";
import {
  CreateBranch,
  FetchDepartament,
  getCompanys,
  MunicipalityByDepartament,
  UpdateBranch,
} from "services";
import * as Yup from "yup";
import { useBranchList } from "../useBranchList";
import { useSessionStore } from "Common/Stores";
import { useTranslation } from "react-i18next";
import {
  getDefaultValueForSelect,
  validatePhoneNumber,
  validateSelect,
  validateYupStringWithMinLegthMaxLength,
} from "helpers/functionsHelpers";
import { useEffect, useState } from "react";

export const useModalBranch = () => {
  const { branch, isEdit, clearAll, setIsOpenModal } = useBranchStore();
  const { session } = useSessionStore();
  const { t } = useTranslation();
  const { refetchBranch } = useBranchList();
  const [valueDepartment, setValueDepartment] = useState<OptionSelect>();
  const [valueMunicipality, setValueMunicipality] = useState<OptionSelect>();
  const [valueCompany, setValueCompany] = useState<OptionSelect>();

  const initialValues: FormBranch = {
    description: branch?.description || "",
    address: branch?.address || "",
    phonenumber: branch?.phonenumber || "",
    company_id: branch?.company.id || session?.employe.branch.company.id || 0,
    municipality_id: branch?.municipality?.id || 0,
    departament_id: branch?.municipality?.departament?.id.toString() || "",
  };

  const validationSchema = Yup.object({
    description: validateYupStringWithMinLegthMaxLength(t),
    address: validateYupStringWithMinLegthMaxLength(t, 5, 200),
    phonenumber: validatePhoneNumber(t),
    company_id: validateSelect(t),
    departament_id: validateSelect(t),
    municipality_id: validateSelect(t),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        const data: RequestBranch = {
          description: values.description,
          address: values.address,
          phonenumber: values.phonenumber,
          company_id: values.company_id,
          municipality_id: values.municipality_id,
        };

        if (isEdit && branch) {
          await updateBranch(data, branch.id);
        } else {
          await createBranch(data);
        }
      } catch (error) {
        throw error;
      } finally {
        formik.resetForm();
        setSubmitting(false);
      }
    },
  });

  const { data: departamentData } = useQuery({
    queryKey: ["departament"],
    queryFn: async () => {
      const response = await FetchDepartament();
      return response.data.map(
        (dept): OptionSelect => ({
          label: dept.description,
          value: dept.id.toString(),
        })
      );
    },
    staleTime: 5000,
  });

  useEffect(() => {
    if (!formik.values.departament_id) {
      setValueDepartment(undefined);
    } else {
      const defaultValueFind = getDefaultValueForSelect(
        departamentData || [],
        formik.values.departament_id
      );
      setValueDepartment(defaultValueFind);
    }
  }, [formik.values.departament_id, departamentData]);

  const { data: municipalitydata, refetch: refetchMunicipality } = useQuery({
    queryKey: ["municipality", formik.values.departament_id],
    queryFn: async () => {
      const response = await MunicipalityByDepartament(
        Number(formik.values.departament_id)
      );
      return response.data.map(
        (muni): OptionSelect => ({
          label: muni.description,
          value: muni.id.toString(),
        })
      );
    },
    enabled: formik.values.departament_id !== "0",
    staleTime: 5000,
  });

  useEffect(() => {
    if (!formik.values.municipality_id) {
      setValueMunicipality(undefined);
    } else {
      const defaultValueFind = getDefaultValueForSelect(
        municipalitydata || [],
        formik.values.municipality_id.toString()
      );
      setValueMunicipality(defaultValueFind);
    }
  }, [formik.values.municipality_id, municipalitydata]);

  const { data: CompanyData } = useQuery({
    queryKey: ["company"],
    queryFn: async () => {
      const response = await getCompanys();
      return response.data.map(
        (company): OptionSelect => ({
          label: company.name,
          value: company.id.toString(),
        })
      );
    },
  });

  useEffect(() => {
    if (formik.values.company_id) {
      const defaultValueFind = getDefaultValueForSelect(
        CompanyData || [],
        formik.values.company_id.toString()
      );
      setValueCompany(defaultValueFind);
    }
  }, [formik.values.company_id, CompanyData]);

  const createBranch = async (data: RequestBranch) => {
    try {
      const response = await CreateBranch(data);

      if (response.code === "200") {
        setIsOpenModal(false);
        refetchBranch();
        clearAll();
        showNotifications(NotificationType.SUCCESS, t("branch.alert.sucess"));
      } else {
        showNotifications(
          NotificationType.ERROR,
          t("branch.alert.error") + response.message
        );
      }
    } catch (error) {
      showNotifications(NotificationType.ERROR, t("branch.alert.error"));
    }
  };

  const updateBranch = async (data: RequestBranch, id: number) => {
    try {
      const response = await UpdateBranch(id, data);

      if (response.code === "200") {
        showNotifications(
          NotificationType.SUCCESS,
          t("branch.alert.update")
        );
        setIsOpenModal(false);
        refetchBranch();
        clearAll();
      } else {
        showNotifications(
          NotificationType.ERROR,
          t("branch.alert.error") + response.message
        );
      }
    } catch (error) {
      showNotifications(NotificationType.ERROR, t("branch.alert.error"));
    }
  };

  return {
    formik,
    municipality: municipalitydata,
    departament: departamentData,
    refetchMunicipality,
    CompanyData,
    isEdit,
    t,
    valueDepartment,
    valueMunicipality,
    valueCompany,
  };
};
